import React, { useState, useEffect } from 'react';
import { 
  ComputerDesktopIcon, 
  CheckCircleIcon, 
  XCircleIcon,
  ArrowPathIcon,
  CloudArrowUpIcon,
  CommandLineIcon,
  DocumentTextIcon,
  ServerIcon,
  PlusIcon
} from '@heroicons/react/24/outline';
import AddServerModal from '../components/AddServerModal';

const ServersManager = () => {
  const [servers, setServers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedServers, setSelectedServers] = useState([]);
  const [updating, setUpdating] = useState(false);
  const [updateResults, setUpdateResults] = useState(null);
  const [commandModal, setCommandModal] = useState({ open: false, serverId: null });
  const [commandOutput, setCommandOutput] = useState('');
  const [addServerModal, setAddServerModal] = useState(false);

  const fetchServers = async () => {
    try {
      const response = await fetch('/api/servers/list', {
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`
        }
      });
      
      if (response.ok) {
        const data = await response.json();
        setServers(data.servers || []);
      }
    } catch (error) {
      console.error('Erro ao buscar servidores:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchServers();
  }, []);

  const testConnection = async (serverId) => {
    try {
      const response = await fetch(`/api/servers/${serverId}/test`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      const result = await response.json();
      
      // Atualizar status do servidor na lista
      setServers(prev => prev.map(s => 
        s.id === serverId 
          ? { ...s, connected: result.success }
          : s
      ));
      
      return result;
    } catch (error) {
      console.error('Erro ao testar conexão:', error);
      return { success: false, message: error.message };
    }
  };

  const updateServer = async (serverId) => {
    try {
      const response = await fetch(`/api/servers/${serverId}/update`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        }
      });
      
      return await response.json();
    } catch (error) {
      console.error('Erro ao atualizar servidor:', error);
      return { success: false, error: error.message };
    }
  };

  const updateAllSelected = async () => {
    if (selectedServers.length === 0) return;
    
    setUpdating(true);
    setUpdateResults(null);
    
    try {
      const response = await fetch('/api/servers/update-all', {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ serverIds: selectedServers })
      });
      
      const result = await response.json();
      setUpdateResults(result);
    } catch (error) {
      console.error('Erro ao atualizar servidores:', error);
      setUpdateResults({ 
        success: false, 
        error: error.message 
      });
    } finally {
      setUpdating(false);
    }
  };

  const executeCommand = async (serverId, command) => {
    try {
      const response = await fetch(`/api/servers/${serverId}/execute`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${localStorage.getItem('token')}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({ command })
      });
      
      const result = await response.json();
      setCommandOutput(`Comando: ${command}\n\nSaída:\n${result.output}\n\nErro:\n${result.error || 'Nenhum'}`);
      return result;
    } catch (error) {
      console.error('Erro ao executar comando:', error);
      setCommandOutput(`Erro ao executar comando: ${error.message}`);
    }
  };

  const handleServerSelection = (serverId) => {
    setSelectedServers(prev => 
      prev.includes(serverId)
        ? prev.filter(id => id !== serverId)
        : [...prev, serverId]
    );
  };

  const selectAllServers = () => {
    setSelectedServers(servers.map(s => s.id));
  };

  const clearSelection = () => {
    setSelectedServers([]);
  };

  const handleServerAdded = () => {
    setAddServerModal(false);
    fetchServers(); // Refresh the servers list
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <ArrowPathIcon className="h-8 w-8 animate-spin text-blue-500" />
        <span className="ml-2 text-gray-600">Carregando servidores...</span>
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Gerenciamento de Servidores</h1>
          <p className="text-gray-600">Gerencie múltiplos servidores de forma centralizada</p>
        </div>
        <div className="flex items-center space-x-3">
          <button
            onClick={() => setAddServerModal(true)}
            className="btn btn-primary"
          >
            <PlusIcon className="h-4 w-4 mr-2" />
            Adicionar Servidor
          </button>
          <button
            onClick={fetchServers}
            className="btn btn-secondary"
          >
            <ArrowPathIcon className="h-4 w-4 mr-2" />
            Atualizar Lista
          </button>
        </div>
      </div>

      {/* Ações em lote */}
      <div className="bg-white p-4 rounded-lg shadow border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-4">
            <span className="text-sm font-medium text-gray-700">
              Ações em lote:
            </span>
            <button
              onClick={selectAllServers}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Selecionar Todos
            </button>
            <button
              onClick={clearSelection}
              className="text-sm text-gray-600 hover:text-gray-800"
            >
              Limpar Seleção
            </button>
            <span className="text-sm text-gray-500">
              ({selectedServers.length} selecionados)
            </span>
          </div>
          
          <button
            onClick={updateAllSelected}
            disabled={selectedServers.length === 0 || updating}
            className="btn btn-primary disabled:opacity-50"
          >
            {updating ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Atualizando...
              </>
            ) : (
              <>
                <CloudArrowUpIcon className="h-4 w-4 mr-2" />
                Atualizar Selecionados
              </>
            )}
          </button>
        </div>
      </div>

      {/* Resultados de atualização */}
      {updateResults && (
        <div className={`p-4 rounded-lg ${updateResults.success ? 'bg-green-50 border-green-200' : 'bg-red-50 border-red-200'} border`}>
          <h3 className="font-medium mb-2">
            {updateResults.success ? 'Atualização Concluída' : 'Erro na Atualização'}
          </h3>
          {updateResults.summary && (
            <p className="text-sm mb-2">
              Total: {updateResults.summary.total} | 
              Sucesso: {updateResults.summary.success} | 
              Falha: {updateResults.summary.failed}
            </p>
          )}
          {updateResults.error && (
            <p className="text-sm text-red-600">{updateResults.error}</p>
          )}
        </div>
      )}

      {/* Lista de servidores */}
      <div className="grid gap-4">
        {servers.map((server) => (
          <div key={server.id} className="bg-white p-4 rounded-lg shadow border">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <input
                  type="checkbox"
                  checked={selectedServers.includes(server.id)}
                  onChange={() => handleServerSelection(server.id)}
                  className="h-4 w-4 text-blue-600 border-gray-300 rounded"
                />
                
                <div className="flex items-center space-x-2">
                  <ComputerDesktopIcon className="h-6 w-6 text-gray-400" />
                  <div>
                    <h3 className="font-medium text-gray-900">{server.name}</h3>
                    <p className="text-sm text-gray-600">{server.host}</p>
                  </div>
                </div>

                <div className="flex items-center space-x-1">
                  {server.connected ? (
                    <>
                      <CheckCircleIcon className="h-5 w-5 text-green-500" />
                      <span className="text-sm text-green-600">Conectado</span>
                    </>
                  ) : (
                    <>
                      <XCircleIcon className="h-5 w-5 text-red-500" />
                      <span className="text-sm text-red-600">Desconectado</span>
                    </>
                  )}
                </div>
              </div>

              <div className="flex items-center space-x-2">
                <button
                  onClick={() => testConnection(server.id)}
                  className="btn btn-sm btn-secondary"
                >
                  <ServerIcon className="h-4 w-4 mr-1" />
                  Testar
                </button>
                
                <button
                  onClick={() => setCommandModal({ open: true, serverId: server.id })}
                  className="btn btn-sm btn-secondary"
                >
                  <CommandLineIcon className="h-4 w-4 mr-1" />
                  Comando
                </button>
                
                <button
                  onClick={() => updateServer(server.id)}
                  className="btn btn-sm btn-primary"
                >
                  <CloudArrowUpIcon className="h-4 w-4 mr-1" />
                  Atualizar
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>

      {/* Modal de comando */}
      {commandModal.open && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden">
            <div className="p-6 border-b">
              <h3 className="text-lg font-medium">Executar Comando</h3>
              <p className="text-sm text-gray-600">
                Servidor: {servers.find(s => s.id === commandModal.serverId)?.name}
              </p>
            </div>
            
            <div className="p-6 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Comando:
                </label>
                <input
                  type="text"
                  placeholder="Ex: ls -la"
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  onKeyPress={(e) => {
                    if (e.key === 'Enter') {
                      executeCommand(commandModal.serverId, e.target.value);
                    }
                  }}
                />
              </div>
              
              {commandOutput && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Saída:
                  </label>
                  <pre className="bg-gray-900 text-green-400 p-4 rounded text-sm overflow-auto max-h-60">
                    {commandOutput}
                  </pre>
                </div>
              )}
            </div>
            
            <div className="p-6 border-t flex justify-end space-x-3">
              <button
                onClick={() => {
                  setCommandModal({ open: false, serverId: null });
                  setCommandOutput('');
                }}
                className="btn btn-secondary"
              >
                Fechar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal para adicionar servidor */}
      <AddServerModal
        isOpen={addServerModal}
        onClose={() => setAddServerModal(false)}
        onServerAdded={handleServerAdded}
      />
    </div>
  );
};

export default ServersManager;